import React, { useState, useEffect, useRef } from 'react';
import {
  PhotoIcon,
  CloudArrowUpIcon,
  TrashIcon,
  MagnifyingGlassIcon,
  CheckCircleIcon,
  XMarkIcon,
  DocumentDuplicateIcon,
  EyeIcon,
  ExclamationCircleIcon,
} from '@heroicons/react/24/outline';
import { blogService } from '../../services/blogService';
import { BlogImage } from '../../types/blog';

interface ImageManagementProps {
  onImageSelect?: (image: BlogImage) => void;
  selectionMode?: boolean;
  onClose?: () => void;
}

const ImageManagement: React.FC<ImageManagementProps> = ({ 
  onImageSelect, 
  selectionMode = false,
  onClose 
}) => {
  const [images, setImages] = useState<BlogImage[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedImage, setSelectedImage] = useState<BlogImage | null>(null);
  const [previewImage, setPreviewImage] = useState<BlogImage | null>(null);
  const [altText, setAltText] = useState('');
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    loadImages();
  }, []);

  const loadImages = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await blogService.getImages();
      if (response.success && response.data) {
        setImages(Array.isArray(response.data) ? response.data : []);
      } else {
        setError(response.error?.message || 'Failed to load images');
      }
    } catch (err) {
      setError('Failed to load images');
      console.error('Error loading images:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = async (files: FileList | null) => {
    if (!files || files.length === 0) return;

    setUploading(true);
    setError(null);

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      
      // Validate file type
      if (!['image/jpeg', 'image/png', 'image/webp', 'image/gif'].includes(file.type)) {
        setError(`Invalid file type: ${file.name}. Only JPEG, PNG, WebP, and GIF are allowed.`);
        continue;
      }

      // Validate file size (10MB max)
      if (file.size > 10 * 1024 * 1024) {
        setError(`File too large: ${file.name}. Maximum size is 10MB.`);
        continue;
      }

      try {
        const response = await blogService.uploadImage(file, altText || file.name);
        if (response.success && response.data) {
          setImages(prev => [response.data!, ...prev]);
        } else {
          setError(response.error?.message || `Failed to upload ${file.name}`);
        }
      } catch (err) {
        setError(`Failed to upload ${file.name}`);
        console.error('Upload error:', err);
      }
    }

    setUploading(false);
    setAltText('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    handleFileSelect(e.dataTransfer.files);
  };

  const handleDelete = async (imageId: string) => {
    if (!window.confirm('Are you sure you want to delete this image?')) return;

    try {
      const response = await blogService.deleteImage(imageId);
      if (response.success) {
        setImages(prev => prev.filter(img => img._id !== imageId));
        if (selectedImage?._id === imageId) {
          setSelectedImage(null);
        }
      } else {
        setError(response.error?.message || 'Failed to delete image');
      }
    } catch (err) {
      setError('Failed to delete image');
      console.error('Delete error:', err);
    }
  };

  const handleImageClick = (image: BlogImage) => {
    if (selectionMode) {
      setSelectedImage(image);
    } else {
      setPreviewImage(image);
    }
  };

  const handleInsertImage = () => {
    if (selectedImage && onImageSelect) {
      onImageSelect(selectedImage);
      setSelectedImage(null);
    }
  };

  const copyImageUrl = (url: string) => {
    // Copy the relative URL - the website will construct the full URL
    navigator.clipboard.writeText(url);
    // Show a brief success message
    setError(null);
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const filteredImages = images.filter(img => 
    img.originalName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    img.altText?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    img.alt?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getImageUrl = (url: string) => {
    if (!url) return '';
    // For display in the dashboard, construct full URL
    if (url.startsWith('http')) return url;
    return `${process.env.REACT_APP_API_URL || 'http://localhost:3002'}${url}`;
  };

  return (
    <div className={`${selectionMode ? '' : 'space-y-6'}`}>
      {/* Header for Selection Mode */}
      {selectionMode && onClose && (
        <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl flex items-center justify-center">
              <PhotoIcon className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-gray-900">Select Image</h3>
              <p className="text-sm text-gray-600">Choose an image to insert into your blog</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <XMarkIcon className="w-6 h-6 text-gray-500" />
          </button>
        </div>
      )}

      {/* Upload Section */}
      <div 
        className={`relative border-2 border-dashed rounded-2xl p-8 text-center transition-all duration-200 ${
          dragActive 
            ? 'border-blue-500 bg-blue-50' 
            : 'border-gray-300 hover:border-blue-400 hover:bg-gray-50'
        }`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif"
          multiple
          onChange={(e) => handleFileSelect(e.target.files)}
          className="hidden"
        />
        
        <div className="flex flex-col items-center gap-4">
          <div className={`w-16 h-16 rounded-full flex items-center justify-center ${
            dragActive ? 'bg-blue-100' : 'bg-gray-100'
          }`}>
            <CloudArrowUpIcon className={`w-8 h-8 ${dragActive ? 'text-blue-600' : 'text-gray-400'}`} />
          </div>
          
          <div>
            <p className="text-lg font-semibold text-gray-700">
              {dragActive ? 'Drop images here' : 'Drag & drop images here'}
            </p>
            <p className="text-sm text-gray-500 mt-1">or click to browse</p>
          </div>

          {/* Alt Text Input */}
          <div className="w-full max-w-md">
            <input
              type="text"
              value={altText}
              onChange={(e) => setAltText(e.target.value)}
              placeholder="Alt text for accessibility (optional)"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
            />
          </div>

          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl hover:from-blue-700 hover:to-purple-700 transition-all duration-200 font-medium disabled:opacity-50 flex items-center gap-2"
          >
            {uploading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                Uploading...
              </>
            ) : (
              <>
                <CloudArrowUpIcon className="w-5 h-5" />
                Upload Images
              </>
            )}
          </button>

          <p className="text-xs text-gray-400">
            Supported: JPEG, PNG, WebP, GIF • Max size: 10MB
          </p>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border-2 border-red-200 text-red-700 px-4 py-3 rounded-xl flex items-center gap-2">
          <ExclamationCircleIcon className="w-5 h-5 flex-shrink-0" />
          <span className="text-sm">{error}</span>
          <button onClick={() => setError(null)} className="ml-auto">
            <XMarkIcon className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Search */}
      <div className="relative">
        <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search images by name or alt text..."
          className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>

      {/* Images Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-4 border-blue-200 border-t-blue-600"></div>
        </div>
      ) : filteredImages.length === 0 ? (
        <div className="text-center py-12">
          <PhotoIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-700 mb-2">
            {searchTerm ? 'No images found' : 'No images uploaded yet'}
          </h3>
          <p className="text-gray-500">
            {searchTerm ? 'Try a different search term' : 'Upload your first image to get started'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
          {filteredImages.map((image) => (
            <div
              key={image._id}
              onClick={() => handleImageClick(image)}
              className={`relative group cursor-pointer rounded-xl overflow-hidden border-2 transition-all duration-200 ${
                selectedImage?._id === image._id
                  ? 'border-blue-500 ring-2 ring-blue-200'
                  : 'border-gray-200 hover:border-blue-300'
              }`}
            >
              <div className="aspect-square bg-gray-100">
                <img
                  src={getImageUrl(image.thumbnailUrl || image.url)}
                  alt={image.altText || image.alt || image.originalName}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = getImageUrl(image.url);
                  }}
                />
              </div>

              {/* Selection Indicator */}
              {selectedImage?._id === image._id && (
                <div className="absolute top-2 right-2 w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
                  <CheckCircleIcon className="w-4 h-4 text-white" />
                </div>
              )}

              {/* Hover Overlay */}
              <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-all duration-200 flex items-center justify-center opacity-0 group-hover:opacity-100">
                <div className="flex gap-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setPreviewImage(image);
                    }}
                    className="p-2 bg-white rounded-lg hover:bg-gray-100 transition-colors"
                    title="Preview"
                  >
                    <EyeIcon className="w-4 h-4 text-gray-700" />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      copyImageUrl(image.url);
                    }}
                    className="p-2 bg-white rounded-lg hover:bg-gray-100 transition-colors"
                    title="Copy URL"
                  >
                    <DocumentDuplicateIcon className="w-4 h-4 text-gray-700" />
                  </button>
                  {!selectionMode && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(image._id);
                      }}
                      className="p-2 bg-white rounded-lg hover:bg-red-100 transition-colors"
                      title="Delete"
                    >
                      <TrashIcon className="w-4 h-4 text-red-600" />
                    </button>
                  )}
                </div>
              </div>

              {/* Image Info */}
              <div className="p-2 bg-white">
                <p className="text-xs font-medium text-gray-700 truncate">
                  {image.originalName}
                </p>
                <p className="text-xs text-gray-500">
                  {formatFileSize(image.size)}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Selection Mode Actions */}
      {selectionMode && selectedImage && (
        <div className="sticky bottom-0 bg-white border-t border-gray-200 p-4 -mx-6 -mb-6 mt-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img
              src={getImageUrl(selectedImage.thumbnailUrl || selectedImage.url)}
              alt={selectedImage.altText || selectedImage.originalName}
              className="w-12 h-12 object-cover rounded-lg"
            />
            <div>
              <p className="text-sm font-medium text-gray-900">{selectedImage.originalName}</p>
              <p className="text-xs text-gray-500">{selectedImage.width}x{selectedImage.height}</p>
            </div>
          </div>
          <button
            onClick={handleInsertImage}
            className="px-6 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl hover:from-blue-700 hover:to-purple-700 transition-all duration-200 font-medium flex items-center gap-2"
          >
            <CheckCircleIcon className="w-5 h-5" />
            Insert Image
          </button>
        </div>
      )}

      {/* Preview Modal */}
      {previewImage && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4"
          onClick={() => setPreviewImage(null)}
        >
          <div 
            className="bg-white rounded-2xl max-w-4xl max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <div>
                <h3 className="font-semibold text-gray-900">{previewImage.originalName}</h3>
                <p className="text-sm text-gray-500">
                  {previewImage.width}x{previewImage.height} • {formatFileSize(previewImage.size)}
                </p>
              </div>
              <button
                onClick={() => setPreviewImage(null)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <XMarkIcon className="w-6 h-6 text-gray-500" />
              </button>
            </div>
            <div className="p-4 overflow-auto max-h-[70vh]">
              <img
                src={getImageUrl(previewImage.url)}
                alt={previewImage.altText || previewImage.originalName}
                className="max-w-full h-auto mx-auto"
              />
            </div>
            <div className="p-4 border-t border-gray-200 flex gap-3">
              <button
                onClick={() => copyImageUrl(previewImage.url)}
                className="flex-1 px-4 py-2 border-2 border-gray-200 rounded-xl hover:bg-gray-50 transition-colors font-medium flex items-center justify-center gap-2"
              >
                <DocumentDuplicateIcon className="w-5 h-5" />
                Copy URL
              </button>
              {selectionMode && onImageSelect && (
                <button
                  onClick={() => {
                    onImageSelect(previewImage);
                    setPreviewImage(null);
                  }}
                  className="flex-1 px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl hover:from-blue-700 hover:to-purple-700 transition-all duration-200 font-medium flex items-center justify-center gap-2"
                >
                  <CheckCircleIcon className="w-5 h-5" />
                  Insert Image
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageManagement;
export type { ImageManagementProps };
